import React from "react";
import { SignUp } from "aws-amplify-react";
import { API } from "aws-amplify";
import { withStyles } from "@material-ui/core/styles";
import {
  Button,
  FormGroup,
  Link,
  Box,
  Typography,
  Snackbar,
  Divider
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AuthTabs from "./AuthTabs";
import ExtendedTextField from "../extended-material-ui/TextField";
import {
  isValidZipCode,
  isValidNickName
} from "../utility/userProfileValidators";

/* Authenticator components can be extended to allow UI customization
 * and in some cases, minor functionality and flow customization.
 *
 * The main method that needs to be overridden for most UI customization is showComponent()
 */

// styles
const styles = theme => ({
  mcButton: {
    ...theme.typography.mcButton
  },
  mcLink: {
    ...theme.mcLink
  }
});

class ExtendedSignUpThemed extends SignUp {
  formFieldHelperText = {
    nickname: "Nickname must have at least 4 letters/numbers",
    email: "This email does not exist. Please enter a valid email",
    "custom:zip_code": "5-digit zip code is required"
  };

  constructor(props) {
    super(props);
    this._validAuthStates = ["signUp"];
    this.state = {
      extSignUpThemedSignUpError: false,
      extSignUpThemedSignUpErrorMessage: "",
      extSignUpThemedSignUpErrorMessageCount: 0
    };
  }

  validEmailCheck = async () => {
    try {
      const response = await API.post("email", "/simple-verification", {
        queryStringParameters: {
          email: this.inputs["email"] || ""
        }
      });
      return response.isValid;
    } catch (e) {
      console.error("Error validating email", e);
      return false;
    }
  };

  validateInputData = async () => {
    let response = {
      status: "", // OK, ERROR
      errorMessages: []
    };

    if (!(await this.validEmailCheck())) {
      response.status = "ERROR";
      response.errorMessages.push(this.formFieldHelperText.email);
    }

    if (!isValidZipCode(this.inputs["zip_code"])) {
      response.status = "ERROR";
      response.errorMessages.push(this.formFieldHelperText["custom:zip_code"]);
    }

    if (!isValidNickName(this.inputs["nickname"])) {
      response.status = "ERROR";
      response.errorMessages.push(this.formFieldHelperText.nickname);
    }

    if (response.status === "" && response.errorMessages.length === 0) {
      response.status = "OK";
    }
    return response;
  };

  hideAlert = () => {
    this.setState({
      extSignUpThemedSignUpError: false,
      extSignUpThemedSignUpErrorMessage: "",
      extSignUpThemedSignUpErrorMessageCount: 0
    });
  };

  signUp = async () => {
    this.inputs = this.inputs || {};
    this.inputs["signup_tenant"] = this.props.site;

    Object.keys(this.inputs).forEach(input => {
      const inputValue = this.inputs[input];
      if (inputValue && typeof inputValue === "string") {
        this.inputs[input] = inputValue.trim();
      }
    });

    const validationResponse = await this.validateInputData();
    if (
      validationResponse.status !== "OK" &&
      validationResponse.errorMessages.length > 0
    ) {
      this.setState({
        extSignUpThemedSignUpError: true,
        extSignUpThemedSignUpErrorMessage: validationResponse.errorMessages.join(
          " -- "
        ),
        extSignUpThemedSignUpErrorMessageCount:
          validationResponse.errorMessages.length
      });
    } else {
      super.signUp();
    }
  };

  signUpWithEnter = async e => {
    if (e.key === "Enter") {
      e.preventDefault();
      await this.signUp();
    }
  };

  handleEmailInputChange = e => {
    this.handleInputChange(e);
    this.inputs = this.inputs || {};
    const { value } = e.target;
    this.inputs["username"] = value;
  };

  showComponent() {
    this.signUpFields = this.props.signUpConfig.signUpFields;
    const { privacyPolicy, visitorAgreement } = this.props;

    return (
      <AuthTabs
        authState={this.props.authState}
        onTabChange={tabValue => super.changeState(tabValue)}
      >
        {this.props.authState === "signUp" && (
          <>
            <Box mb={2}>
              <p>All fields are required.</p>
              <FormGroup>
                <form>
                  <ExtendedTextField
                    id="email"
                    key="email"
                    name="email"
                    type="text"
                    label="Email Address"
                    onChange={this.handleEmailInputChange}
                    onKeyPress={this.signUpWithEnter}
                    color="secondary"
                    required
                  />
                  <ExtendedTextField
                    id="username"
                    key="username"
                    name="username"
                    type="text"
                    label="User Name"
                    onChange={this.handleInputChange}
                    style={{ display: "none" }}
                    required
                  />
                  <ExtendedTextField
                    id="password"
                    key="password"
                    name="password"
                    type="password"
                    label="Password"
                    onChange={this.handleInputChange}
                    onKeyPress={this.signUpWithEnter}
                    color="secondary"
                    required
                  />
                  <ExtendedTextField
                    id="nickname"
                    key="nickname"
                    name="nickname"
                    type="text"
                    label="Display Name"
                    onChange={this.handleInputChange}
                    onKeyPress={this.signUpWithEnter}
                    color="secondary"
                    required
                  />
                  <ExtendedTextField
                    id="given_name"
                    key="given_name"
                    name="given_name"
                    type="text"
                    label="First Name"
                    onChange={this.handleInputChange}
                    onKeyPress={this.signUpWithEnter}
                    color="secondary"
                    required
                  />
                  <ExtendedTextField
                    id="family_name"
                    key="family_name"
                    name="family_name"
                    type="text"
                    label="Last Name"
                    onChange={this.handleInputChange}
                    onKeyPress={this.signUpWithEnter}
                    color="secondary"
                    required
                  />
                  <ExtendedTextField
                    id="birthdate"
                    key="birthdate"
                    name="birthdate"
                    type="date"
                    label="Birth Date"
                    InputLabelProps={{ shrink: true }}
                    onChange={this.handleInputChange}
                    onKeyPress={this.signUpWithEnter}
                    color="secondary"
                    required
                  />
                  <ExtendedTextField
                    id="zip_code"
                    key="zip_code"
                    name="zip_code"
                    type="text"
                    label="Zip Code (5 digits)"
                    inputProps={{ maxLength: 10 }}
                    onChange={this.handleInputChange}
                    onKeyPress={this.signUpWithEnter}
                    color="secondary"
                    required
                  />
                  <Box my={2}>
                    <Button
                      className={this.props.classes.mcButton}
                      size="large"
                      variant="contained"
                      fullWidth
                      onClick={this.signUp}
                    >
                      Create Your Profile
                    </Button>
                  </Box>
                </form>
              </FormGroup>
              <Snackbar
                open={this.state.extSignUpThemedSignUpError}
                autoHideDuration={
                  3000 * this.state.extSignUpThemedSignUpErrorMessageCount
                }
                onClose={this.hideAlert}
              >
                <Alert onClose={this.hideAlert} severity="error">
                  {this.state.extSignUpThemedSignUpErrorMessage}
                </Alert>
              </Snackbar>
              <Divider />
              <Typography variant="subtitle1">
                By submitting your registration information, you agree to our{" "}
                <Link
                  className={this.props.classes.mcLink}
                  href={visitorAgreement}
                  target="_blank"
                  rel="noreferrer"
                >
                  Visitor Agreement
                </Link>{" "}
                and{" "}
                <Link
                  className={this.props.classes.mcLink}
                  href={privacyPolicy}
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </Link>
                .
              </Typography>
            </Box>
          </>
        )}
      </AuthTabs>
    );
  }
}

export const ExtendedSignUp = withStyles(styles, { withTheme: true })(
  ExtendedSignUpThemed
);
